import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaSearch, FaTimes, FaLink } from 'react-icons/fa';
import { FaPlay, FaTag, FaMapMarkerAlt } from 'react-icons/fa';
import { isStoreOpen } from '../../utils/storeTimeUtils';
import { searchProducts, clearProducts, fetchProducts, fetchOfferProducts, setOffersMode } from '../../redux/menu/ProductSlice';
import useTheme from '../../hooks/useTheme';
import { getNavbarBackgroundColor } from '../../utils/backgroundStyles';

const Navbar = () => {
    const [changeHeader, setChangeHeader] = useState(false)
    const [isSearchExpanded, setIsSearchExpanded] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [isMobile, setIsMobile] = useState(false)
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const theme = useTheme();
    const { profile } = useSelector((state) => state.profile);
    const { categories } = useSelector(state => state.category);
    const { status, isSearchMode, isOffersMode } = useSelector(state => state.product);
    const prevIsSearchMode = useRef(isSearchMode);
    
    // Get navbar background color based on coverImage
    const coverImage = profile?.cover_image || profile?.theme?.coverImage || 'foody';
    const navbarBgColor = getNavbarBackgroundColor(coverImage);
    const isNoBackground = coverImage === 'noBackground';
    
    // Check if we're on the home screen (route pattern: /:id)
    const isHomeScreen = location.pathname === `/${profile?.key}` || location.pathname === '/';
    
    // Check if store is currently open
    const storeIsOpen = isStoreOpen(profile?.from_time, profile?.to_time);
    
    // Check if store status is available (not false)
    const storeStatusAvailable = profile?.status !== false;
    
    // Store is available for orders if it's open and status is available
    const storeAvailable = storeIsOpen && storeStatusAvailable;

    //header change function 
    const onChangeHeader = () => {
        if (window.scrollY >= 50) {
            setChangeHeader(true)
        } else {
            setChangeHeader(false)
        }
    }

    //change header by scrolling
    window.addEventListener('scroll', onChangeHeader)

    // Check if device is mobile
    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768)
        }
        
        checkIsMobile()
        window.addEventListener('resize', checkIsMobile)
        
        return () => window.removeEventListener('resize', checkIsMobile)
    }, [])

    const handleReels = () => {
        navigate(`${profile?.key}/reels`);
    };

    const handleConnect = () => {
        navigate(`${profile?.key}/connect`);
    };

    const handleMap = () => {
        if (profile?.map_url) {
            // Open Google Maps link in new tab
            window.open(profile.map_url, '_blank', 'noopener,noreferrer');
        } else {
            // Fallback to connect page if no map URL
            navigate(`${profile?.key}/connect`);
        }
    };

    const handleOffers = () => {
        if (isOffersMode) {
            // If already in offers mode, toggle back to regular products
            dispatch(setOffersMode(false));
            // Load first category products
            if (categories && categories.length > 0) {
                dispatch(clearProducts());
                dispatch(fetchProducts({ 
                    categoryID: categories[0].id, 
                    page: 1 
                }));
            }
        } else {
            // If not in offers mode, show offers
            dispatch(setOffersMode(true));
            dispatch(fetchOfferProducts({}));
        }
    };

    // Search functionality
    const handleSearch = () => { 
        if (searchText.trim()) {
            // Clear previous products and search for new ones
            dispatch(clearProducts())
            
            // Dispatch search action
            dispatch(searchProducts({ 
                text: searchText.trim() 
            })).then((result) => {
               
            })
        } else {
            // If search text is empty, return to initial state
            handleClearSearch()
        }
    }

    const handleClearSearch = useCallback(() => {
        // Clear search mode and load initial products
        if (categories && categories.length > 0) {
            dispatch(clearProducts())
            dispatch(fetchProducts({ 
                categoryID: categories[0].id, 
                page: 1 
            }))
        }
        setSearchText('')
        setIsSearchExpanded(false)
    }, [categories, dispatch])

    // Handle when search text is cleared
    useEffect(() => {
        if (searchText === '' && isSearchMode) {
            handleClearSearch()
        }
    }, [searchText, isSearchMode, handleClearSearch])

    // Handle when search mode is cleared externally (e.g., from category selection)
    useEffect(() => {
        // Only clear search text when isSearchMode changes from true to false
        if (prevIsSearchMode.current === true && isSearchMode === false && searchText !== '') {
            // Clear the search text when search mode is turned off externally
            setSearchText('')
            setIsSearchExpanded(false)
        }
        // Update the ref to current value
        prevIsSearchMode.current = isSearchMode;
    }, [isSearchMode, searchText])

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const toggleSearch = () => {
        setIsSearchExpanded(!isSearchExpanded)
        if (isSearchExpanded) {
            handleClearSearch()
        }
    }

    return (
        <header className={changeHeader || isNoBackground ? "fixed z-50 top-0 left-0 w-full transition duration-500" : "bg-transparent fixed z-50 top-0 left-0 w-full transition duration-500"} style={{ backgroundColor: (changeHeader || isNoBackground) ? (isNoBackground ? '#Fafafa' : navbarBgColor) : 'transparent', boxShadow: 'none' }}>
            <nav className="flex items-center max-w-screen-xl mx-auto px-3 sm:px-4 md:px-6 py-2 md:py-3">
                {/* left  */}
                <div className="flex flex-grow">
                    <img 
                        className="w-16 h-14 sm:w-18 sm:h-16 md:w-20 md:h-16 lg:w-28 lg:h-24 cursor-pointer object-contain" 
                        src={profile?.DocLogo?.imageUrl } 
                        alt={profile?.name || "logo"} 
                        onClick={() => navigate(`/${profile?.key}`)} 
                    />
                </div>
                {/* right  */}
                {storeAvailable && (
                    <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-3">
                        {/* Search Button/Input - Only show on home screen */}
                        {isHomeScreen && (
                            <div className="relative">
                                {/* Mobile: Always show search input, Desktop: Show expandable search */}
                                {isMobile ? (
                                    // Mobile: Always expanded search
                                    <div className="flex items-center space-x-1 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm px-2 py-1.5">
                                        <input 
                                            type="text" 
                                            className="outline-none bg-transparent text-xs w-20 text-gray-700 placeholder-gray-400" 
                                            placeholder="Search..." 
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                        />
                                        <button 
                                            onClick={handleSearch}
                                            className={`text-xs py-0.5 px-2 rounded-full poppins transition duration-300 ${
                                                status === 'loading' && isSearchMode ? 'bg-gray-400 cursor-not-allowed' : ''
                                            }`}
                                            style={status !== 'loading' || !isSearchMode ? {
                                                backgroundColor: theme.colors.searchButtonBg,
                                                color: theme.colors.searchButtonText,
                                                border: `1px solid ${theme.colors.searchButtonBorder}`
                                            } : {}}
                                            disabled={status === 'loading' && isSearchMode}
                                        >
                                            {status === 'loading' && isSearchMode ? '...' : 'Go'}
                                        </button>
                                        {searchText && (
                                            <button 
                                                onClick={handleClearSearch}
                                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                                title="Clear search"
                                            >
                                                <FaTimes className="w-2.5 h-2.5" />
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    // Desktop: Expandable search
                                    <>
                                        {!isSearchExpanded ? (
                                            <button 
                                                onClick={toggleSearch}
                                                className="flex items-center space-x-2 px-3 md:px-4 py-1.5 md:py-2.5 bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-700 rounded-full shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105 hover:border-gray-300 hover:bg-white"
                                                title="Search"
                                            >
                                                <FaSearch className="w-4 h-4" style={{ color: theme.colors.searchIconColor }} />
                                                <span className="poppins font-medium text-sm" style={{ color: theme.colors.searchTextColor }}>Search</span>
                                            </button>
                                        ) : (
                                            <div className="flex items-center space-x-2 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm px-3 md:px-4 py-1.5 md:py-2.5">
                                                <input 
                                                    type="text" 
                                                    className="outline-none bg-transparent text-sm w-32 lg:w-48 text-gray-700 placeholder-gray-400" 
                                                    placeholder="Search..." 
                                                    value={searchText}
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                    onKeyPress={handleKeyPress}
                                                    autoFocus
                                                />
                                                <button 
                                                    onClick={handleSearch}
                                                    className={`text-xs py-1 px-3 rounded-full poppins transition duration-300 ${
                                                        status === 'loading' && isSearchMode ? 'bg-gray-400 cursor-not-allowed' : ''
                                                    }`}
                                                    style={status !== 'loading' || !isSearchMode ? {
                                                        backgroundColor: theme.colors.searchButtonBg,
                                                        color: theme.colors.searchButtonText,
                                                        border: `1px solid ${theme.colors.searchButtonBorder}`
                                                    } : {}}
                                                    disabled={status === 'loading' && isSearchMode}
                                                >
                                                    {status === 'loading' && isSearchMode ? '...' : 'Go'}
                                                </button>
                                                <button 
                                                    onClick={toggleSearch}
                                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                                    title="Close search"
                                                >
                                                    <FaTimes className="w-3 h-3" />
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Mobile Offers Button - Only show on home screen and mobile */}
                        {isHomeScreen && isMobile && (
                            <button 
                                onClick={handleOffers}
                                className="flex items-center space-x-1 px-2 py-1.5 rounded-full shadow-sm transition-all duration-300"
                                style={{
                                    backgroundColor: isOffersMode ? theme.colors.navOffersBgActive : theme.colors.navOffersBgInactive,
                                    borderColor: isOffersMode ? theme.colors.navOffersBorderActive : theme.colors.navOffersBorderInactive,
                                    borderWidth: '1px',
                                    borderStyle: 'solid'
                                }}
                                title="View Offers"
                            >
                                <FaTag 
                                    className="w-3 h-3" 
                                    style={{ color: isOffersMode ? theme.colors.navOffersIconActive : theme.colors.navOffersIconInactive }}
                                />
                                <span 
                                    className="text-xs font-medium"
                                    style={{ color: isOffersMode ? theme.colors.navOffersIconActive : theme.colors.navOffersIconInactive }}
                                >
                                    Offers
                                </span>
                            </button>
                        )}

                        {/* Offers Button - Only show on home screen */}
                        {isHomeScreen && (
                            <button 
                                onClick={handleOffers}
                                className="hidden md:flex items-center space-x-2 px-3 md:px-4 py-1.5 md:py-2.5 rounded-full shadow-sm transform transition-all duration-300 hover:scale-105"
                                style={{
                                    backgroundColor: isOffersMode ? theme.colors.navOffersBgActive : theme.colors.navOffersBgInactive,
                                    borderColor: isOffersMode ? theme.colors.navOffersBorderActive : theme.colors.navOffersBorderInactive,
                                    borderWidth: '1px',
                                    borderStyle: 'solid'
                                }}
                                title="View Offers"
                            >
                                <FaTag 
                                    className="w-3 h-3 md:w-4 md:h-4" 
                                    style={{ color: isOffersMode ? theme.colors.navOffersIconActive : theme.colors.navOffersIconInactive }}
                                />
                                <span 
                                    className="hidden lg:block poppins font-medium text-sm"
                                    style={{ color: isOffersMode ? theme.colors.navOffersIconActive : theme.colors.navOffersIconInactive }}
                                >
                                    Offers
                                </span>
                            </button>
                        )}
                        {/* Reels or Map Button - Show based on reel count */}
                        {profile?.reel > 0 ? (
                            <button 
                                onClick={handleReels}
                                className="hidden md:flex items-center space-x-2 px-3 md:px-4 py-1.5 md:py-2.5 rounded-full shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105"
                                style={{
                                    backgroundColor: location.pathname.includes('/reels') ? theme.colors.navReelsBgActive : theme.colors.navReelsBgInactive,
                                    borderColor: location.pathname.includes('/reels') ? theme.colors.navReelsBorderActive : theme.colors.navReelsBorderInactive,
                                    borderWidth: '1px',
                                    borderStyle: 'solid'
                                }}
                                title="View Reels"
                            >
                                <FaPlay 
                                    className="w-3 h-3 md:w-4 md:h-4" 
                                    style={{ color: location.pathname.includes('/reels') ? theme.colors.navReelsIconActive : theme.colors.navReelsIconInactive }} 
                                />
                                <span 
                                    className="hidden lg:block poppins font-medium text-sm" 
                                    style={{ color: location.pathname.includes('/reels') ? theme.colors.navReelsIconActive : theme.colors.navReelsIconInactive }}
                                >
                                    Reels
                                </span>
                            </button>
                        ) : (
                            <button 
                                onClick={handleMap}
                                className="hidden md:flex items-center space-x-2 px-3 md:px-4 py-1.5 md:py-2.5 rounded-full shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105"
                                style={{
                                    backgroundColor: theme.colors.navMapBgInactive,
                                    borderColor: theme.colors.navMapBorderInactive,
                                    borderWidth: '1px',
                                    borderStyle: 'solid'
                                }}
                                title="View Map"
                            >
                                <FaMapMarkerAlt 
                                    className="w-3 h-3 md:w-4 md:h-4" 
                                    style={{ color: theme.colors.navMapIconInactive }} 
                                />
                                <span 
                                    className="hidden lg:block poppins font-medium text-sm" 
                                    style={{ color: theme.colors.navMapIconInactive }}
                                >
                                    Map
                                </span>
                            </button>
                        )}
                        <button 
                            onClick={handleConnect}
                            className="hidden md:flex items-center space-x-2 px-3 md:px-4 py-1.5 md:py-2.5 rounded-full shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105"
                            style={{
                                backgroundColor: location.pathname.includes('/connect') ? theme.colors.navConnectBgActive : theme.colors.navConnectBgInactive,
                                borderColor: location.pathname.includes('/connect') ? theme.colors.navConnectBorderActive : theme.colors.navConnectBorderInactive,
                                borderWidth: '1px',
                                borderStyle: 'solid'
                            }}
                            title="Connect with us"
                        >
                            <FaLink 
                                className="w-3 h-3 md:w-4 md:h-4" 
                                style={{ color: location.pathname.includes('/connect') ? theme.colors.navConnectIconActive : theme.colors.navConnectIconInactive }} 
                            />
                            <span 
                                className="hidden lg:block poppins font-medium text-sm" 
                                style={{ color: location.pathname.includes('/connect') ? theme.colors.navConnectIconActive : theme.colors.navConnectIconInactive }}
                            >
                                Connect
                            </span>
                        </button>
                    </div>
                )}

            </nav>
        </header>
    )
}

export default Navbar
